import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import { useTranslation, Trans } from "react-i18next";
import SorteoOpinionForm from "./SorteoOpinionForm";
import SorteoPrivacyPolicy from "./SorteoPrivacyPolicy";
import RetroBackground from "../../components/layout/RetroBackground";

// Botón innovador con efecto neón
const NeonButton = ({ children, onClick, variant = "primary" }) => {
    const baseClasses = "relative px-8 py-3 font-bold text-lg rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95";
    const variants = {
        primary: "bg-gradient-to-r from-yellow-500 to-orange-500 text-gray-900 shadow-lg hover:shadow-yellow-500/50",
        secondary: "bg-transparent border-2 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 hover:border-yellow-400",
    };

    return (
        <motion.button
            onClick={onClick}
            className={`${baseClasses} ${variants[variant]}`}
            whileHover={{ boxShadow: variant === "primary" ? "0 0 30px rgba(250, 204, 21, 0.5)" : "none" }}
            whileTap={{ scale: 0.95 }}
        >
            {children}
        </motion.button>
    );
};

const SorteoPage = () => {
    const { t } = useTranslation();
    const [currentView, setCurrentView] = useState("landing");

    const handleFormSuccess = () => {
        setCurrentView("success");
    };

    const handleBack = () => {
        setCurrentView("landing");
    };

    const pageVariants = {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
    };

    const pageTransition = { duration: 0.5 };

    return (
        <>
            <Helmet>
                <title>{t("sorteo.title")}</title>
                <meta name="description" content={t("sorteo.description")} />
            </Helmet>

            <div
                className="fixed inset-0 bg-cover bg-center"
                style={{
                    backgroundImage: "url('/zaquiel-fondo-1920x1080.webp')",
                    backgroundSize: "cover",
                    backgroundPosition: "right center",
                }}
            >
                <RetroBackground />
                <div className="absolute inset-0 bg-black bg-opacity-70"></div>

                <AnimatePresence>
                    {/* Landing Page */}
                    {currentView === "landing" && (
                        <motion.div
                            key="landing"
                            className="absolute inset-0 flex items-center justify-center"
                            variants={pageVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={pageTransition}
                        >
                            <div className="relative z-10 container mx-auto px-4 py-8 flex flex-col items-center justify-center">
                                {/* Header */}
                                <motion.div
                                    initial={{ y: -30, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                    className="text-center mb-8"
                                >
                                    <motion.div
                                        animate={{ rotate: [0, 5, -5, 0] }}
                                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                        className="text-6xl md:text-8xl mb-4"
                                    >
                                        🎁
                                    </motion.div>
                                    <h1 className="text-4xl md:text-6xl font-bold text-yellow-500 mb-4">
                                        {t("sorteo.exclusiveRaffle")}
                                    </h1>
                                    <p className="text-xl md:text-2xl text-white max-w-2xl mx-auto">
                                        {t("sorteo.participateAndWin")}
                                    </p>
                                </motion.div>

                                {/* Tarjeta principal */}
                                <motion.div
                                    initial={{ y: 30, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.3 }}
                                    className="w-full max-w-2xl"
                                >
                                    <div className="backdrop-blur-md bg-white/10 rounded-2xl p-6 md:p-8 shadow-2xl">
                                        {/* Cómo participar */}
                                        <div className="mb-6">
                                            <h2 className="text-2xl font-bold text-yellow-400 mb-4">
                                                📋 {t("sorteo.howToParticipate")}
                                            </h2>
                                            <ul className="space-y-3 text-white">
                                                {[
                                                    { num: 1, key: "sorteo.step1" },
                                                    { num: 2, key: "sorteo.step2" },
                                                    { num: 3, key: "sorteo.step3" },
                                                ].map((item, i) => (
                                                    <motion.li
                                                        key={item.num}
                                                        initial={{ x: -20, opacity: 0 }}
                                                        animate={{ x: 0, opacity: 1 }}
                                                        transition={{ delay: 0.5 + i * 0.1 }}
                                                        className="flex items-start gap-3"
                                                    >
                                                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-500/80 flex items-center justify-center font-bold text-gray-900">
                                                            {item.num}
                                                        </span>
                                                        <span>
                                                            <Trans i18nKey={item.key} components={{ 1: <strong className="text-yellow-400" /> }} />
                                                        </span>
                                                    </motion.li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Botón principal */}
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 1 }}
                                            className="text-center"
                                        >
                                            <NeonButton onClick={() => setCurrentView("form")}>
                                                {t("sorteo.participateNow")}
                                            </NeonButton>
                                        </motion.div>

                                        {/* Aviso importante - DEBAJO del botón */}
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 1.1 }}
                                            className="mt-6 text-center"
                                        >
                                            <p className="text-yellow-200/70 text-sm">
                                                ⚠️ <Trans i18nKey="sorteo.emailWarning" components={{ 1: <strong /> }} />
                                            </p>
                                        </motion.div>
                                    </div>
                                </motion.div>

                                {/* Footer */}
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 1.2 }}
                                    className="mt-6 text-center"
                                >
                                    <p className="mb-2 text-white/70 text-sm">
                                        {t("sorteo.privacyNote")}{" "}
                                        <button
                                            onClick={() => setCurrentView("privacy")}
                                            className="text-yellow-400 hover:underline"
                                        >
                                            {t("sorteo.privacyLink")}
                                        </button>
                                    </p>
                                    <p>
                                        <a href="/" className="text-yellow-400/70 hover:text-yellow-400 text-sm">
                                            {t("sorteo.backToComic")}
                                        </a>
                                    </p>
                                </motion.div>
                            </div>
                        </motion.div>
                    )}

                    {/* Form Page */}
                    {currentView === "form" && (
                        <motion.div
                            key="form"
                            className="absolute inset-0 overflow-y-auto"
                            variants={pageVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={pageTransition}
                            style={{ WebkitOverflowScrolling: "touch" }}
                        >
                            <div className="relative z-10 w-full min-h-full py-6 px-4">
                                <SorteoOpinionForm
                                    onBack={handleBack}
                                    onSuccess={handleFormSuccess}
                                    onShowPrivacy={() => setCurrentView("privacy")}
                                />
                            </div>
                        </motion.div>
                    )}

                    {/* Privacy Page - Centrado verticalmente */}
                    {currentView === "privacy" && (
                        <motion.div
                            key="privacy"
                            className="absolute inset-0 flex items-center justify-center overflow-y-auto"
                            variants={pageVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={pageTransition}
                            style={{ WebkitOverflowScrolling: "touch" }}
                        >
                            <SorteoPrivacyPolicy onBack={handleBack} />
                        </motion.div>
                    )}

                    {/* Success Page */}
                    {currentView === "success" && (
                        <motion.div
                            key="success"
                            className="absolute inset-0 flex items-center justify-center"
                            variants={pageVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={pageTransition}
                        >
                            <div className="relative z-10 text-center p-8">
                                <motion.div
                                    animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
                                    transition={{ duration: 0.5, repeat: 3 }}
                                    className="text-8xl mb-6"
                                >
                                    🎉
                                </motion.div>
                                <h1 className="text-4xl md:text-5xl font-bold text-yellow-500 mb-4">
                                    {t("sorteo.successTitle")}
                                </h1>
                                <p className="text-xl text-white mb-6 max-w-md mx-auto">
                                    {t("sorteo.successMessage")}
                                </p>
                                <p className="text-lg text-yellow-300/80 mb-8">
                                    {t("sorteo.successHint")}
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <NeonButton onClick={() => setCurrentView("form")}>
                                        {t("sorteo.rateAnother")}
                                    </NeonButton>
                                    <NeonButton onClick={() => window.location.href = "/"} variant="secondary">
                                        {t("sorteo.backToHome")}
                                    </NeonButton>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </>
    );
};

export default SorteoPage;
